/**
 * Execution Signal Clustering — pure deterministic aggregator
 * ────────────────────────────────────────────────────────────
 * Passive pattern recognition over canonical execution sinks. Observational
 * only — no scoring, no AI, no recommendations, no writers, no telemetry.
 *
 * Hard rules:
 *   • Pure function. No React. No mutations.
 *   • Never carries financial or personal data (only IDs + display labels).
 *   • Singleton clusters (count < MIN_SAMPLE_FOR_DISPLAY) are dropped.
 *   • Deterministic order: count desc, then label asc, then id asc.
 */

import { MIN_SAMPLE_FOR_DISPLAY } from "@/lib/analyticsThresholds";
import type { Callback } from "@/data/contractor/networkExecution/callback";
import type { HoldbackLedgerEntry } from "@/data/contractor/networkExecution/holdback/ledger";
import type { FieldMaterialFeedbackEvent } from "@/data/VendorPOContext";
import type { JobSpecExecution } from "@/data/jobSpecExecution";
import type { Contractor } from "@/data/types";

export type SignalClusterGroup = "installer" | "job" | "scheduling";

export type SignalClusterKind =
  | "installer_callbacks"
  | "installer_holdbacks"
  | "installer_field_issues"
  | "installer_qa_fails"
  | "callback_type"
  | "job_hotspot";

export interface SignalCluster {
  id: string;
  group: SignalClusterGroup;
  kind: SignalClusterKind;
  /** Compact human label, e.g. "Wesley HVAC — 3 QA fails". */
  label: string;
  /** Underlying count (already ≥ MIN_SAMPLE_FOR_DISPLAY). */
  count: number;
  /** Optional sub-line context. */
  detail?: string;
}

export interface BuildSignalClustersInput {
  callbacks: Callback[];
  holdbackLedger: HoldbackLedgerEntry[];
  fieldMaterialFeedback: FieldMaterialFeedbackEvent[];
  specExecutions: JobSpecExecution[];
  contractors: Contractor[];
  /** Optional map jobId → installer contractor id (for QA-fail attribution). */
  jobInstallerMap?: Record<string, string | null | undefined>;
}

export interface SignalClustersResult {
  installer: SignalCluster[];
  job: SignalCluster[];
  scheduling: SignalCluster[];
  total: number;
}

/* ── helpers ──────────────────────────────────────────────────────────── */

function inc(map: Map<string, number>, key: string): void {
  map.set(key, (map.get(key) ?? 0) + 1);
}

function contractorLabel(id: string, contractors: Contractor[]): string {
  const c = contractors.find((x) => x.id === id);
  return c?.companyName ?? id;
}

function sortClusters(arr: SignalCluster[]): SignalCluster[] {
  return [...arr].sort((a, b) => {
    if (a.count !== b.count) return b.count - a.count;
    if (a.label !== b.label) return a.label.localeCompare(b.label);
    return a.id.localeCompare(b.id);
  });
}

/* ── builder ──────────────────────────────────────────────────────────── */

export function buildExecutionSignalClusters(
  input: BuildSignalClustersInput,
): SignalClustersResult {
  const {
    callbacks,
    holdbackLedger,
    fieldMaterialFeedback,
    specExecutions,
    contractors,
    jobInstallerMap = {},
  } = input;

  /* Installer-attributed callbacks. */
  const cbByInstaller = new Map<string, number>();
  for (const c of callbacks) {
    if (c.installerContractorIdAtCallback) {
      inc(cbByInstaller, c.installerContractorIdAtCallback);
    }
  }

  /* Installer-attributed holdbacks (count of ledger actions per contractor). */
  const hbByInstaller = new Map<string, number>();
  for (const h of holdbackLedger) {
    if (h.contractorId) inc(hbByInstaller, h.contractorId);
  }

  /* Installer-attributed field material issues (actor key as-is). */
  const ffByActor = new Map<string, number>();
  for (const f of fieldMaterialFeedback) {
    if (f.actor) inc(ffByActor, f.actor);
  }

  /* Installer-attributed QA fails via jobInstallerMap. */
  const qaFailByInstaller = new Map<string, number>();
  for (const exec of specExecutions) {
    const installer = jobInstallerMap[exec.jobId];
    if (!installer) continue;
    for (const p of exec.phases) {
      if (p.status === "qa_failed") inc(qaFailByInstaller, installer);
    }
  }

  /* Callback-type clusters across all jobs. */
  const cbByType = new Map<string, number>();
  for (const c of callbacks) inc(cbByType, c.type);

  /* Job hotspots — total event count per jobId across canonical sinks. */
  const eventsByJob = new Map<string, number>();
  for (const c of callbacks) inc(eventsByJob, c.jobId);
  for (const h of holdbackLedger) inc(eventsByJob, h.jobId);
  for (const f of fieldMaterialFeedback) inc(eventsByJob, f.jobId);
  for (const exec of specExecutions) {
    for (const p of exec.phases) {
      if (p.status === "qa_failed") inc(eventsByJob, exec.jobId);
    }
  }

  /* ── materialize clusters (gated by MIN_SAMPLE_FOR_DISPLAY) ───────── */

  const installer: SignalCluster[] = [];
  for (const [id, count] of cbByInstaller) {
    if (count < MIN_SAMPLE_FOR_DISPLAY) continue;
    const label = contractorLabel(id, contractors);
    installer.push({
      id: `inst-cb:${id}`,
      group: "installer",
      kind: "installer_callbacks",
      label: `${label} — ${count} callbacks`,
      count,
    });
  }
  for (const [id, count] of hbByInstaller) {
    if (count < MIN_SAMPLE_FOR_DISPLAY) continue;
    const label = contractorLabel(id, contractors);
    installer.push({
      id: `inst-hb:${id}`,
      group: "installer",
      kind: "installer_holdbacks",
      label: `${label} — ${count} holdback actions`,
      count,
    });
  }
  for (const [id, count] of qaFailByInstaller) {
    if (count < MIN_SAMPLE_FOR_DISPLAY) continue;
    const label = contractorLabel(id, contractors);
    installer.push({
      id: `inst-qa:${id}`,
      group: "installer",
      kind: "installer_qa_fails",
      label: `${label} — ${count} QA fails`,
      count,
    });
  }
  for (const [actor, count] of ffByActor) {
    if (count < MIN_SAMPLE_FOR_DISPLAY) continue;
    installer.push({
      id: `inst-ff:${actor}`,
      group: "installer",
      kind: "installer_field_issues",
      label: `${actor} — ${count} field issues`,
      count,
    });
  }

  const job: SignalCluster[] = [];
  for (const [type, count] of cbByType) {
    if (count < MIN_SAMPLE_FOR_DISPLAY) continue;
    job.push({
      id: `cb-type:${type}`,
      group: "job",
      kind: "callback_type",
      label: `${count} callbacks — ${type.replace(/_/g, " ")}`,
      count,
    });
  }
  for (const [jobId, count] of eventsByJob) {
    if (count < MIN_SAMPLE_FOR_DISPLAY) continue;
    job.push({
      id: `job-hot:${jobId}`,
      group: "job",
      kind: "job_hotspot",
      label: `${jobId} — ${count} execution events`,
      count,
    });
  }

  /* Scheduling clusters intentionally empty in this pass — canonical
   * scheduling-block source not yet normalized into the rail. Slot
   * reserved so consumers can render the group header deterministically. */
  const scheduling: SignalCluster[] = [];

  const sortedInstaller = sortClusters(installer);
  const sortedJob = sortClusters(job);
  const sortedScheduling = sortClusters(scheduling);

  return {
    installer: sortedInstaller,
    job: sortedJob,
    scheduling: sortedScheduling,
    total: sortedInstaller.length + sortedJob.length + sortedScheduling.length,
  };
}

export const SIGNAL_CLUSTER_GROUP_LABEL: Record<SignalClusterGroup, string> = {
  installer: "Installer patterns",
  job: "Job patterns",
  scheduling: "Scheduling patterns",
};
